import React, { useState, useRef, useEffect, useCallback, useId } from 'react';
import { AIChatMessage, AIChatSession, wyscoutMatchesAPI } from '@/services/api';
import { ChatPreset, AIChatPanelProps } from './AIChatPanel/types';
import ChatToggleButton from './AIChatPanel/ChatToggleButton';
import SavedSessionsList from './AIChatPanel/SavedSessionsList';
import PlayerFocusSelect from './AIChatPanel/PlayerFocusSelect';
import MessageList from './AIChatPanel/MessageList';
import ChatInputBar from './AIChatPanel/ChatInputBar';
import DrawerHeader from './AIChatPanel/DrawerHeader';
import PresetButtons from './AIChatPanel/PresetButtons';

const focusableSelector = [
  'a[href]',
  'area[href]',
  'button:not([disabled])',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  'iframe',
  'object',
  'embed',
  '[contenteditable="true"]',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

const isVisibleFocusable = (element: HTMLElement): boolean => {
  if (element.tabIndex < 0 || element.matches(':disabled') || element.getAttribute('aria-disabled') === 'true') return false;
  const checkVisibility = (element as HTMLElement & { checkVisibility?: (options?: { checkOpacity?: boolean; checkVisibilityCSS?: boolean }) => boolean }).checkVisibility;
  if (checkVisibility) return checkVisibility.call(element, { checkOpacity: true, checkVisibilityCSS: true });
  for (let current: HTMLElement | null = element; current; current = current.parentElement) {
    const style = window.getComputedStyle(current);
    if (current.hidden || current.hasAttribute('inert') || current.getAttribute('aria-hidden') === 'true' || style.display === 'none' || style.visibility === 'hidden' || style.visibility === 'collapse' || style.opacity === '0') return false;
  }
  return true;
};

const AIChatPanel: React.FC<AIChatPanelProps> = ({
  onSendMessage,
  onSendLatestMessage,
  presets,
  playerOptions,
  onPlayerFocusChange,
  title = 'AI Analysis',
  entityType,
  entityId,
  open,
  onOpenChange,
  initialSessionId,
  listSessions,
  getSessionMessages,
  deleteSession,
  maxMessageLength,
}) => {
  const [messages, setMessages] = useState<AIChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [internalOpen, setInternalOpen] = useState(false);
  const isOpen = open ?? internalOpen;
  const drawerHeadingId = useId();
  const changeOpen = useCallback((next: boolean) => {
    if (open === undefined) setInternalOpen(next);
    onOpenChange?.(next);
  }, [open, onOpenChange]);
  const [selectedPlayer, setSelectedPlayer] = useState<number | null>(null);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [savedSessions, setSavedSessions] = useState<AIChatSession[]>([]);
  const [showSavedSessions, setShowSavedSessions] = useState(false);
  const [loadingSessions, setLoadingSessions] = useState(false);
  const [sessionsError, setSessionsError] = useState<string | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const drawerRef = useRef<HTMLDivElement>(null);
  const restoreFocusRef = useRef<HTMLElement | null>(null);
  const wasOpen = useRef(false);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  useEffect(() => {
    let focusTimer: ReturnType<typeof setTimeout> | undefined;
    if (isOpen && !wasOpen.current) {
      restoreFocusRef.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
      focusTimer = setTimeout(() => inputRef.current?.focus(), 300);
    } else if (!isOpen && wasOpen.current) {
      restoreFocusRef.current?.focus();
      restoreFocusRef.current = null;
    }
    wasOpen.current = isOpen;
    return () => {
      if (focusTimer) clearTimeout(focusTimer);
    };
  }, [isOpen]);

  const fetchSessions = useCallback(async () => {
    setLoadingSessions(true);
    setSessionsError(null);
    try {
      let sessions: AIChatSession[];
      if (listSessions) sessions = await listSessions();
      else if (entityType === 'match') sessions = (await wyscoutMatchesAPI.listMatchChatSessions(Number(entityId))).data;
      else if (entityType === 'player') sessions = (await wyscoutMatchesAPI.listPlayerChatSessions(entityId)).data;
      else throw new Error('League analysis requires session callbacks.');
      setSavedSessions((sessions || []).slice(0, 50));
    } catch {
      setSessionsError('Unable to load saved analysis sessions.');
    } finally {
      setLoadingSessions(false);
    }
  }, [entityType, entityId, listSessions]);

  // Fetch sessions when drawer opens
  useEffect(() => {
    if (isOpen) {
      fetchSessions();
    }
  }, [isOpen, fetchSessions]);

  // Auto-open and load a specific session when navigated from chat history
  const initialLoadDone = useRef(false);
  useEffect(() => {
    if (!initialSessionId || initialLoadDone.current) return;
    initialLoadDone.current = true;
    changeOpen(true);
    (async () => {
      try {
        if (entityType === 'league_match' && !getSessionMessages) throw new Error('League analysis requires session callbacks.');
        const records = getSessionMessages ? await getSessionMessages(initialSessionId) : (await wyscoutMatchesAPI.getChatSessionMessages(initialSessionId)).data;
        const loadedMessages: AIChatMessage[] = (records || []).map(m => ({
          role: m.role as 'user' | 'assistant',
          content: m.content,
        }));
        setMessages(loadedMessages.slice(-100));
        setSessionId(initialSessionId);
      } catch {
        setError('Failed to load chat session');
      }
    })();
  }, [changeOpen, entityType, getSessionMessages, initialSessionId]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        changeOpen(false);
        return;
      }
      if (e.key !== 'Tab' || !isOpen || !drawerRef.current) return;
      const drawer = drawerRef.current;
      const focusable = Array.from(drawer.querySelectorAll<HTMLElement>(focusableSelector)).filter(isVisibleFocusable);
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const active = document.activeElement;
      const focusIsTrapped = active instanceof HTMLElement && focusable.includes(active);
      const target = !focusIsTrapped ? (e.shiftKey ? last : first) : e.shiftKey && active === first ? last : !e.shiftKey && active === last ? first : null;
      if (target) {
        e.preventDefault();
        target.focus();
      } else if (!focusable.length) {
        e.preventDefault();
        drawer.focus();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [changeOpen, isOpen]);

  const sendMessage = async (content: string, preset?: string) => {
    if (loading) return;

    const userMessage: AIChatMessage = { role: 'user', content };
    const updatedMessages = [...messages, userMessage];
    setMessages(updatedMessages);
    setInput('');
    setError(null);
    setLoading(true);

    try {
      const result = onSendLatestMessage
        ? await onSendLatestMessage(content, preset, sessionId ?? undefined)
        : await onSendMessage!(updatedMessages, preset, sessionId ?? undefined);
      setMessages(prev => [...prev, { role: 'assistant' as const, content: result.reply }].slice(-100));
      if (result.sessionId) {
        const isNew = !sessionId;
        setSessionId(result.sessionId);
        if (isNew) {
          fetchSessions();
        }
      }
    } catch (err: any) {
      const payload = err?.response?.data?.error;
      const errorMsg = typeof payload === 'string' ? payload : payload?.message || err?.message || 'Failed to get AI response';
      setError(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const handlePresetClick = (preset: ChatPreset) => {
    sendMessage(preset.label, preset.id);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!input.trim() || (maxMessageLength !== undefined && Array.from(input.trim()).length > maxMessageLength)) {
      if (input.trim()) setError(`Message must be ${maxMessageLength} characters or fewer.`);
      return;
    }
    sendMessage(input.trim());
  };

  const handleClear = () => {
    setMessages([]);
    setError(null);
    setSessionId(null);
  };

  const handlePlayerChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;
    const playerId = value === '' ? null : parseInt(value, 10);
    setSelectedPlayer(playerId);
    onPlayerFocusChange?.(playerId);
  };

  const handleLoadSession = async (session: AIChatSession) => {
    try {
      if (entityType === 'league_match' && !getSessionMessages) throw new Error('League analysis requires session callbacks.');
      const records = getSessionMessages ? await getSessionMessages(session.id) : (await wyscoutMatchesAPI.getChatSessionMessages(session.id)).data;
      const loadedMessages: AIChatMessage[] = (records || []).map(m => ({
        role: m.role as 'user' | 'assistant',
        content: m.content,
      }));
      setMessages(loadedMessages.slice(-100));
      setSessionId(session.id);
      setError(null);
    } catch {
      setError('Failed to load chat session');
    }
  };

  const handleDeleteSession = async (id: string) => {
    try {
      if (deleteSession) await deleteSession(id);
      else if (entityType === 'league_match') throw new Error('League analysis requires session callbacks.');
      else await wyscoutMatchesAPI.deleteChatSession(id);
      setSavedSessions(prev => prev.filter(s => s.id !== id));
      if (sessionId === id) {
        handleClear();
      }
      setDeleteConfirm(null);
    } catch {
      setError('Failed to delete session');
    }
  };

  return (
    <>
      {/* Floating toggle button */}
      <ChatToggleButton
        isOpen={isOpen}
        title={title}
        messages={messages}
        onToggle={() => changeOpen(!isOpen)}
      />

      {/* Backdrop overlay (mobile) */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/20 z-40 sm:hidden"
          onClick={() => changeOpen(false)}
          role="button"
          tabIndex={-1}
          aria-label="Close chat panel"
        />
      )}

      {/* Side drawer */}
      <div
        ref={drawerRef}
        role="dialog"
        tabIndex={-1}
        aria-labelledby={drawerHeadingId}
        aria-modal={isOpen}
        aria-hidden={!isOpen}
        inert={!isOpen}
        className={`fixed top-0 right-0 z-40 h-full w-full sm:w-[420px] bg-white dark:bg-gray-900 shadow-2xl transform transition-transform duration-300 ease-in-out flex flex-col ${
          isOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        {/* Drawer header */}
        <DrawerHeader headingId={drawerHeadingId} title={title} onClose={() => changeOpen(false)} />

        {/* Drawer body - scrollable */}
        <div className="flex-1 overflow-y-auto px-4 py-3">
          {/* Saved Sessions */}
          {(savedSessions.length > 0 || loadingSessions) && (
            <SavedSessionsList
              savedSessions={savedSessions}
              loadingSessions={loadingSessions}
              showSavedSessions={showSavedSessions}
              sessionId={sessionId}
              deleteConfirm={deleteConfirm}
              onToggleShow={() => setShowSavedSessions(!showSavedSessions)}
              onLoadSession={handleLoadSession}
              onDeleteSession={handleDeleteSession}
              onSetDeleteConfirm={setDeleteConfirm}
            />
          )}
          {sessionsError && <p role="alert" className="mb-3 rounded-md bg-red-50 p-2 text-sm text-red-700 dark:bg-red-900/20 dark:text-red-300">{sessionsError}</p>}

          {/* Continuing saved chat indicator */}
          {sessionId && messages.length > 0 && (
            <div className="mb-3 flex items-center justify-between px-3 py-1.5 bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-700 rounded-md text-xs">
              <span className="text-purple-700 dark:text-purple-300">Continuing saved chat</span>
              <button
                onClick={handleClear}
                className="text-purple-600 dark:text-purple-400 hover:text-purple-800 dark:hover:text-purple-200 font-medium"
              >
                Start new chat
              </button>
            </div>
          )}

          {/* Player focus dropdown */}
          {playerOptions && playerOptions.length > 0 && (
            <PlayerFocusSelect
              playerOptions={playerOptions}
              selectedPlayer={selectedPlayer}
              onChange={handlePlayerChange}
            />
          )}

          {/* Presets - shown before first message */}
          {messages.length === 0 && (
            <PresetButtons
              presets={presets}
              loading={loading}
              onPresetClick={handlePresetClick}
            />
          )}

          {/* Messages area */}
          {messages.length > 0 && (
            <MessageList
              messages={messages}
              loading={loading}
              messagesEndRef={messagesEndRef}
            />
          )}

          {/* Error display */}
          {error && (
            <div className="mt-3 p-2 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
              <p className="text-sm text-red-700 dark:text-red-300">{error}</p>
            </div>
          )}
        </div>

        {/* Drawer footer - input bar pinned to bottom */}
        <ChatInputBar
          messages={messages}
          presets={presets}
          input={input}
          loading={loading}
          inputRef={inputRef}
          onPresetClick={handlePresetClick}
          onClear={handleClear}
          onInputChange={setInput}
          onSubmit={handleSubmit}
          maxMessageLength={maxMessageLength}
        />
      </div>
    </>
  );
};

export default AIChatPanel;
